import {ofType} from 'redux-observable';
import {concat, EMPTY, iif, of} from 'rxjs';
import {mergeMap} from 'rxjs/operators';
import {AnyAction} from 'redux';
import qs from 'query-string';
import * as UTMActions from '../actions/UTMActions';
import {EXPIRATION_TIME_COOKIE, NAME_COOKIE} from '../constants/MotorcreditConstants';
import {timeInMinutesForCookie, typeByDevice} from '../../common/utils/Utils';
import {Cookie} from '../types/mx/creditoReal/epics';
import MobileDetect from 'mobile-detect';
import * as CookieUtils from '../../common/utils/CookieUtils';
import * as StateConstants from '../constants/StateConstants';
import * as CalculatorActions from '../actions/CalculatorActions';
import {isEmpty} from '../../common/utils/ValidationUtils';
import * as UIActions from '../actions/UIActions';
import {COUNTRIES} from '../constants/AppConstants';
import {VehiclePremiumInfo} from '../types/mx/catalog';
import {decode} from 'jsonwebtoken';

export const getParamsFromUrl = (action$, state$) =>
    action$.pipe(ofType(UTMActions.GET_PARAMS_URL_ACTION.name),
        mergeMap((action: AnyAction) => {
            const params = qs.parse(action.url);
            const price = params['price'] || CookieUtils.getCookie(StateConstants.PRICE) || 0;
            const downPayment = params['down-payment'] || CookieUtils.getCookie(StateConstants.DOWN_PAYMENT) || 0;
            const year = params['year'] || CookieUtils.getCookie(StateConstants.YEAR_SELECTED) || '';
            const brand = params['brand'] || CookieUtils.getCookie(StateConstants.BRAND_SELECTED) || '';
            const model = params['model'] || CookieUtils.getCookie(StateConstants.MODEL_SELECTED) || '';
            const version = params['version'] || CookieUtils.getCookie(StateConstants.VERSION_SELECTED) || '';
            const province = params['postal-code'] || CookieUtils.getCookie(StateConstants.POSTAL_CODE) || '';
            const hasInsurance = CookieUtils.getCookie(StateConstants.HAS_INSURANCE) ?
                JSON.parse(CookieUtils.getCookie(StateConstants.HAS_INSURANCE)) :
                state$.value.creditoReal.calculator.hasInsurance;

            const vpiQS = params['vpi'] ||
                CookieUtils.getCookie(StateConstants.VEHICLE_PREMIUM_INFO) || '';
            const isUrlFromPI = !!vpiQS;
            const vpiInfo: VehiclePremiumInfo = isUrlFromPI ? decode(vpiQS.toString()) as VehiclePremiumInfo :
                {dealerId: '', dealerName: '', city: '', vehicleId: '', originalPhoto: '', thumbnailPhoto: ''};

            const idVehicle = (isUrlFromPI && vpiInfo.vehicleId)
                || params['id-vehicle'] || CookieUtils.getCookie(StateConstants.ID_VEHICLE) || '';
            const groupName = (isUrlFromPI && vpiInfo.dealerName)
                || params['group-name'] || CookieUtils.getCookie(StateConstants.GROUP_NAME) || '';
            const groupId = (isUrlFromPI && vpiInfo.dealerId)
                || params['group-id'] || CookieUtils.getCookie(StateConstants.GROUP_ID) || '';
            const groupLocation = (isUrlFromPI && vpiInfo.city)
                || params['group-location'] || CookieUtils.getCookie(StateConstants.GROUP_LOCATION) || '';
            const originalPhoto = (isUrlFromPI && vpiInfo.originalPhoto)
                || CookieUtils.getCookie(StateConstants.ORIGINAL_PHOTO) || '';
            const thumbnailPhoto = (isUrlFromPI && vpiInfo.thumbnailPhoto)
                || CookieUtils.getCookie(StateConstants.THUMBNAIL_PHOTO) || '';

            return concat(
                iif(
                    () => (!isEmpty(province) && !isEmpty(version) && !isEmpty(year) &&
                        !isEmpty(price) && !isEmpty(downPayment)),
                    of(CalculatorActions.CALCULATE_QUOTAS_MX_ACTION
                        .action(province, version, year, hasInsurance, price, downPayment)),
                    EMPTY
                ),
                of(UTMActions.SET_VEHICLE_UTM_VALUES_ON_SIMULATOR
                    .action(
                        price, downPayment, year, brand, model, version, province, hasInsurance, idVehicle, groupName,
                        groupId, groupLocation, isUrlFromPI, originalPhoto, thumbnailPhoto, vpiQS
                    )),
                of(UIActions.INIT_APP_ACTION.action(COUNTRIES.MX.id))
            );
        })
    );

export const getUTMParamsFromUrl = action$ =>
    action$.pipe(ofType(UTMActions.GET_UTM_URL_ACTION.name),
        mergeMap((action: AnyAction) => {
            const params = qs.parse(action.url);
            const utmSource = params['utm_source'] || '';
            const utmMedium = params['utm_medium'] || '';
            const utmCampaign = params['utm_campaign'] || '';
            const utmTerm = params['utm_term'] || '';
            const utmContent = params['utm_content'] || '';
            const gclid = params['gclid'] || '';

            const aUtmUrl = window.location.origin + action.url;
            const aHttpRefer = document.referrer;
            const userAgent = navigator.userAgent;

            const deviceDetect: MobileDetect = new MobileDetect(userAgent);
            const isPhone: string = deviceDetect.phone();
            const isTablet: string = deviceDetect.tablet();
            const typeDevice: string = typeByDevice(isTablet, isPhone);
            const operatingSystem: string = deviceDetect.os() || navigator.platform;
            const resolutionWidth: number = window.screen.width;
            const resolutionHeight: number = window.screen.height;
            const cookie: Cookie = !!CookieUtils.getCookie(NAME_COOKIE) && JSON.parse(CookieUtils.getCookie(NAME_COOKIE));
            const hostName = window.location.hostname;
            const existFirstReferer: boolean = !!cookie.firstReferer;
            let firstReferer = '';

            if (aHttpRefer && (aHttpRefer.indexOf(hostName) === -1)) {
                firstReferer = aHttpRefer;
            } else if (aHttpRefer.indexOf(hostName) >= 0 || !existFirstReferer || !aHttpRefer) {
                firstReferer = 'Direct';
            }

            if (!!utmSource || !!utmMedium || !!utmCampaign || !!utmTerm || !!utmContent || !!gclid) {
                CookieUtils.setCookie(StateConstants.UTM_SOURCE, utmSource, 1);
                CookieUtils.setCookie(StateConstants.UTM_MEDIUM, utmMedium, 1);
                CookieUtils.setCookie(StateConstants.UTM_CAMPAIGN, utmCampaign, 1);
                CookieUtils.setCookie(StateConstants.UTM_TERM, utmTerm, 1);
                CookieUtils.setCookie(StateConstants.UTM_CONTENT, utmContent, 1);
                CookieUtils.setCookie(StateConstants.GCLID, gclid, 1);
            }
            if (firstReferer) {
                CookieUtils.setCookie(
                    NAME_COOKIE,
                    JSON.stringify({aUtmUrl, firstReferer} as Cookie),
                    EXPIRATION_TIME_COOKIE
                );
            }

            return concat(
                of(UTMActions.SET_UTM_VALUES_ACTION.action(aHttpRefer, userAgent, typeDevice, operatingSystem,
                    resolutionWidth, resolutionHeight))
            );
        })
    );
